import { SavedShipmentResponseDataDTO } from "../api/response.types";
import { SealineNameModel } from "./types";
import { SeaReferenceTypesDTO, SeaStatusesModel } from "entities/shared/seaCard.types";
import {
  CoordinateModel,
  DateTimeModel,
  SearchReferenceModel,
  ShipmentVariantModel,
} from "entities/shared/shipmentCard.types";
import { calcProgress } from "entities/shared/utils";
import { BaseModel } from "shared/api/lib/baseModel";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";

import { SavedShipmentResponseParser } from "../api/parser";

export const isSeaSavedShipmentModel = (model: Nullable<{ responseType: string }>): model is SeaSavedCardModel =>
  model?.responseType === ShipmentVariantModel.SeaSaved;

export class SeaSavedCardModel extends BaseModel<SavedShipmentResponseParser, SavedShipmentResponseDataDTO> {
  public readonly responseType: ShipmentVariantModel;

  constructor(DTO: SavedShipmentResponseDataDTO) {
    super({ apiParser: new SavedShipmentResponseParser(DTO), DTO });

    this.responseType = ShipmentVariantModel.SeaSaved;
  }

  get id(): number {
    return this.apiParser.id;
  }

  get reference(): SearchReferenceModel | null {
    return this.apiParser.reference;
  }

  get referenceType(): ValueOf<typeof SeaReferenceTypesDTO> | null {
    return this.apiParser.referenceType;
  }

  get containersAmount(): number {
    return this.apiParser.containersAmount;
  }

  get status(): ValueOf<typeof SeaStatusesModel> {
    return this.apiParser.status ?? SeaStatusesModel.UNKNOWN;
  }

  get progress(): number {
    return calcProgress({
      startDate: this.atd ?? this.etd,
      endDate: this.ata ?? this.eta ?? this.predictiveETA,
      seaStatus: this.status,
    });
  }

  get carrierName(): SealineNameModel | null {
    return this.apiParser.carrierName;
  }

  get carrierScac(): SealineNameModel | null {
    return this.apiParser.carrierScac;
  }

  get fromDirection(): string | null {
    return this.apiParser.fromDirection;
  }

  get toDirection(): string | null {
    return this.apiParser.toDirection;
  }

  get snapshotCreatedAt(): string | null {
    return this.apiParser.snapshotCreatedAt;
  }

  get updatedAt(): string {
    return this.apiParser.updatedAt;
  }

  get currentPin(): CoordinateModel | null {
    return this.apiParser.currentPin;
  }

  get atd(): DateTimeModel | null {
    return this.apiParser.atd;
  }

  get etd(): DateTimeModel | null {
    return this.apiParser.etd;
  }

  get ata(): DateTimeModel | null {
    return this.apiParser.ata;
  }

  get eta(): DateTimeModel | null {
    return this.apiParser.eta;
  }

  get tags(): string[] {
    return this.apiParser.tags;
  }

  get predictiveETA(): DateTimeModel | null {
    return this.apiParser.predictiveETA;
  }

  get containersSizeTypes(): string[] {
    return this.apiParser.containersSizeTypes;
  }
}
